import { isIntent, type Intent } from "./intents.js";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const E164_RE = /^\+[1-9]\d{7,14}$/;
// NANP-only: +1 followed by exactly 10 digits. The SMS channel (Wave 2) can only serve US
// numbers, so capture enforces the same bound server-side — a direct API call with +44…
// must 400, not silently store an unservable number (spec contact-phone-consent AC-1.1).
export const NANP_E164_RE = /^\+1\d{10}$/;
export const MAX_EMAIL_LEN = 254;
export const MAX_CONSENT_VERSION_LEN = 32;

export interface ValidatedContact {
  name: string;
  email: string;
  mobile?: string;
  intent: Intent;
  preferredChannel: "email" | "sms";
  smsConsentGiven: boolean;
  clientConsentTextVersion?: string;
  disclosureVersion: string;
}

export type ValidationError = { field: string; message: string };

export type ValidationResult =
  | { ok: true; value: ValidatedContact }
  | { ok: false; errors: ValidationError[] };

export function validateContact(body: unknown, disclosureVersion: string): ValidationResult {
  const errors: ValidationError[] = [];
  if (typeof body !== "object" || body === null) {
    return { ok: false, errors: [{ field: "_body", message: "must be a JSON object" }] };
  }
  const b = body as Record<string, unknown>;

  const name = typeof b.name === "string" ? b.name.trim() : "";
  if (!name) errors.push({ field: "name", message: "required" });

  const emailRaw = typeof b.email === "string" ? b.email.trim() : "";
  const email = emailRaw.toLowerCase();
  if (!emailRaw || emailRaw.length > MAX_EMAIL_LEN || !EMAIL_RE.test(emailRaw)) {
    errors.push({ field: "email", message: "valid email required (<=254 chars)" });
  }

  let mobile: string | undefined;
  if (b.mobile !== undefined && b.mobile !== null && b.mobile !== "") {
    if (typeof b.mobile !== "string" || !NANP_E164_RE.test(b.mobile)) {
      errors.push({ field: "mobile", message: "must be a US number in E.164 form (+1 and 10 digits)" });
    } else {
      mobile = b.mobile;
    }
  }

  if (!isIntent(b.intent)) errors.push({ field: "intent", message: "not one of the 8 intents" });

  // Channel: email-only until the SMS channel ships (Wave 2 lifts this).
  if (b.preferredChannel !== undefined && b.preferredChannel !== "email") {
    errors.push({ field: "preferredChannel", message: 'only "email" is accepted' });
  }
  const preferredChannel = "email" as const;

  // Consent is voluntary and independent of the phone number (TCPA: consent must never be a
  // condition of providing contact details). Consent WITHOUT a number is meaningless -> 400.
  const smsConsentGiven = b.smsConsent === true;
  if (smsConsentGiven && mobile === undefined) {
    errors.push({ field: "smsConsent", message: "smsConsent requires a mobile number" });
  }

  let clientConsentTextVersion: string | undefined;
  if (b.consentTextVersion !== undefined && b.consentTextVersion !== null) {
    if (typeof b.consentTextVersion !== "string" || b.consentTextVersion.length > MAX_CONSENT_VERSION_LEN) {
      errors.push({ field: "consentTextVersion", message: `must be a string of at most ${MAX_CONSENT_VERSION_LEN} chars` });
    } else {
      clientConsentTextVersion = b.consentTextVersion;
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      name,
      email,
      mobile,
      intent: b.intent as Intent,
      preferredChannel,
      smsConsentGiven,
      clientConsentTextVersion,
      disclosureVersion,
    },
  };
}
